// components/Portal.jsx — مكوّن Portal واحد لكل العناصر العائمة (الجولة
// الختامية 2026-10، البند 4). يحلّ مشكلة iPad حيث لوحات مثل الإشعارات كانت
// تُركَّب داخل شجرة DOM الأصلية فتقع تحت الشريط العلوي الثابت (.hdr/.mob-nav)
// بسبب z-index حرفي منخفض ومحلي السياق. Portal يُركِّب في عنصر واحد ثابت
// في body مباشرة، بزindex من سلّم lib/zIndex.js حصراً (zVar) — صفر أرقام
// حرفية جديدة (يفرضه حارس tests/test_zindex_title_lint_guards.py).
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { zVar } from "../lib/zIndex";

let _rootEl = null;
function getPortalRoot() {
  if (_rootEl && document.body.contains(_rootEl)) return _rootEl;
  _rootEl = document.getElementById("app-portal-root");
  if (!_rootEl) {
    _rootEl = document.createElement("div");
    _rootEl.id = "app-portal-root";
    // ثابت الموضع عند 0,0 بلا ارتفاع فعلي؛ بدونها يرث الحاوية إزاحة التدفّق
    // الطبيعي من نهاية body (قد تكون آلاف البكسلات أسفل الشاشة الحالية)،
    // وبعض محركات العرض (خصوصاً لقطة الشاشة الصامتة Headless) تُخطئ وقتها
    // في تركيب أطفالها position:fixed ضمن حدود الفيوبورت الحقيقية.
    _rootEl.style.position = "fixed";
    _rootEl.style.inset = "0";
    _rootEl.style.width = "0";
    _rootEl.style.height = "0";
    // تصحيح A3 (الجولة الرابعة، 2026-10-12) — السبب الفعلي المتبقي بعد رفع
    // --z-tooltip: #app-portal-root نفسه (position:fixed) كان بلا z-index
    // صريح (auto)، فلا يُنشئ سياق تكديس خاصاً به؛ فتُحتسَب طبقاته الداخلية
    // ضمن ترتيب التكديس العادي (bucket "auto") عند جذر body — أقل من أي
    // عنصر آخر بقيمة z-index صريحة موجبة (مثل POSPage.jsx عند 1500)، مهما
    // كانت قيمة الطبقة الداخلية (tooltip=10001) أعلى اسمياً. دليل حي (جدول
    // ضغطات ⓘ + إعادة إنتاج WebKit): opened_on_top=false ثابتة لكل علامات
    // BranchCustomerCards رغم الحاوية الداخلية الصحيحة. الإصلاح: Root نفسه
    // يحمل أعلى قيمة في السلّم فيُنشئ سياق تكديسه الخاص عند جذر body مباشرة
    // — كل الطبقات الداخلية تبقى مرتّبة بصحّة نسبية فيما بينها كما هي.
    _rootEl.style.zIndex = zVar("tooltip");
    document.body.appendChild(_rootEl);
  }
  return _rootEl;
}

// level: "dropdown" | "drawer" | "modal" | "notification" | "toast" | "tooltip"
//
// إصلاح iPad (2026-10-09، البند 3 من تدقيق لمس حقيقي) — كان root يُهيَّأ
// عبر useEffect (بعد الرسم)، فتُركَّب الفقاعة/اللوحة فعلياً دورة رسم واحدة
// بعد ظهور open=true — أي useLayoutEffect في المستهلك (مثل تقييد موضع
// InfoTip داخل حدود الشاشة) يقيس bubbleRef.current=null في تلك اللحظة
// فيتجاهَل الإصلاح بصمت. getPortalRoot() عملية DOM خالصة ومتكرّرة بأمان
// (idempotent) فتهيئتها أثناء الرسم نفسه (lazy initializer) آمنة؛ الفقاعة
// تُركَّب من أول رسم فيعمل قياس/تقييد الموضع في نفس الدورة.
export function Portal({ children, level = "modal" }) {
  const [root] = useState(() => getPortalRoot());
  if (!root) return null;
  return createPortal(
    <div data-testid="portal-layer" data-portal-level={level}
         style={{ position: "fixed", inset: 0, zIndex: zVar(level) }}>
      {children}
    </div>,
    root
  );
}
